// @ts-strict-ignore
import { LMModel } from './lm-model';
import { AppConstants, LMTransformOptions } from '@utils/index';
import { BehaviorSubject, Subject } from 'rxjs';

export class
LMModelField {
  private _applyState$ = new Subject<boolean>();
  private _clearState$ = new Subject<boolean>();
  private _errorMessage$ = new Subject<string>();
  private _valueChanged$: BehaviorSubject<any>;

  private _decSize: number;
  private _displayFieldHistory = true;
  private _filterType: LMFilterFieldOperator;
  private _defaultFilterType: LMFilterFieldOperator;
  private _fireInitialValue = false;
  private _grpDelimiter: string;
  private _isCompoundField = false;
  private _isNestedCompoundField = false;
  private _isMultiSelectKeyField = false;
  private _isManualRepField = false;
  private _label: any;
  private _maxSelected: number;
  private _members: Array<any>;
  private _missingImage = false;
  private _multiPartFile: File | Blob;
  private _mutable = true;
  private _mutexFieldId: string;
  private _originalValue: any;
  private _precision: number;
  // tri-state: null, false, true; needs to be initialized to null
  private _required: boolean = null;
  private _scannable: boolean;
  private _scannableName: string;
  private _size: number;
  private _stateValues: Array<LMStateValue>;
  private _value: any;
  private _docTitleFieldId: string;
  private _mimeTypeFieldId: string;
  private _mimeType: string;
  private _filterCriteria: LMFilterFieldCriteria;
  private _isNumericArray: boolean;
  private _enteredFilterValue: string;
  private _dependentTimeZone: string[] = [];
  private _exportFilterCriteria: boolean;
  private _disableTranslationFiltering: boolean;

  constructor(private specField: LMSpecField,
    private lmModel: LMModel) {
  }

  // private methods
  /**
   * Initialize properties
   */
  private initialize() {
    // boolean properties are initialized appropriately (true, false, or null)
    // all other properties are set to undefined
    this._decSize = undefined;
    this._displayFieldHistory = true;
    // TODO: Need soho controls to add a reset for filter type, since we don't have access to the drop down.
    // For now, don't reset the value here so the filter type will match what is displayed on the UI
    // https://github.com/infor-design/enterprise/issues/1181
    // this._filterType = undefined;
    this._fireInitialValue = false;
    this._grpDelimiter = undefined;
    this._isCompoundField = false;
    this._isNestedCompoundField = false;
    this._isMultiSelectKeyField = false;
    this._isManualRepField = false;
    this._label = undefined;
    this._maxSelected = undefined;
    this._members = undefined;
    this._missingImage = false;
    this._multiPartFile = undefined;
    this._mutable = true;
    this._mutexFieldId = undefined;
    this._originalValue = undefined;
    this._precision = undefined;
    this._required = null;
    this._scannable = false;
    this._scannableName = undefined;
    this._size = undefined;
    this._stateValues = undefined;
    this._value = undefined;
    this._docTitleFieldId = undefined;
    this._mimeTypeFieldId = undefined;
    this._mimeType = undefined;
    this._filterCriteria = undefined;
    this._isNumericArray = false;
    this._exportFilterCriteria = false;
  }

  /**
   * Transforms localized data into Landmark data
   * @param value
   * @param specField
   * @param transformOptions
   * @returns
   * @private
   */
  private _toDataValue(value: any, specField: LMSpecField, transformOptions?: LMTransformOptions): any {
    // NOTE: EXCEPTION - state field values are not transformed in the model; they need to use the data value
    // because the value on each member/option also uses the data value
    if (specField.hasOwnProperty('stateValues') || specField.hasUserDefinedStates || this.isCompoundField) {
      return value;
    }

    return this.lmModel.lmModelServiceAdapter.toDataValue(value, specField.type, transformOptions);
  }

  // private populateMultiSelectStateValues(dvField: LMField, stateValues: Array<LMStateValue>, isSearchForm: boolean): Array<LMStateValue> {
  //   if (isSearchForm) {
  //     // Dropdowns should show up as multiselect on a search form, so get the correct values formatted and selected
  //     let values = dvField.value as Array<string>;
  //     if (!Array.isArray(values)) {
  //       values = [ values ];
  //     }
  //     return this.lmModel.lmModelServiceAdapter.fieldStatesToMultiOptionsArray(stateValues, values);
  //   }
  //
  //   return stateValues;
  // }

  // public getters/setters for member variables
  public get applyState(): Subject<boolean> {
    return this._applyState$;
  }

  public get clearState(): Subject<boolean> {
    return this._clearState$;
  }

  public get displayFieldHistory() {
    return this._displayFieldHistory;
  }

  public get errorMessage(): Subject<string> {
    return this._errorMessage$;
  }

  public get valueChanged(): BehaviorSubject<any> {
    if (this._valueChanged$) {
      return this._valueChanged$;
    } else {
      this._valueChanged$ = new BehaviorSubject<any>(null);
      return this._valueChanged$;
    }
  }

  public get evaluateIsDirty(): boolean {
    if (this.specField.type === AppConstants.BINARYDOCUMENT) {
      return !!(this.multiPartFile || this._originalValue !== this._value);
    }

    // LMCLIENT-30041: need to re-join the member values to properly evaluate if the multi-select field is dirty as
    // this._value is an array of selected values and this._originalValue is a string of selected values with the delimiter
    if (this.specField.hasUserDefinedStates && this.specField.isMultiselect && !this._isMultiSelectKeyField) {
      const delimiter = this._grpDelimiter || '';
      const memberFieldSize = this.lmModel.getField(this.members[0]).size;
      const val = this._value.map((memberValue: string) => {
        if (!delimiter) {
          // delimiter is undefined. This means that spaces have been added to each value up to the field size. Pad out the member
          // field values to match

          if (this.isNumericArray) {
            // member field values are numeric, pad with 0s
            return memberValue.padStart(memberFieldSize, '0');
          }

          // member field value is a string, pad with spaces
          return memberValue.padEnd(memberFieldSize);
        }
        return memberValue;
      }).join(delimiter);
      return !(this._originalValue === val);
    }

    // using non-strict equality to evaluate 0, '0', 0.00, '0.00' as zero, etc
    // eslint-disable-next-line eqeqeq
    return !(this._originalValue == this._value);
  }

  public get filterType(): LMFilterFieldOperator {
    return this._filterType;
  }

  public set filterType(type: LMFilterFieldOperator) {
    this._filterType = type;
  }

  public get alphaFieldDefaultType(): LMFilterFieldOperator {
    return this._defaultFilterType;
  }

  public get filterCriteria(): LMFilterFieldCriteria {
    return this._filterCriteria;
  }

  public get fireInitialValue() {
    return this._fireInitialValue;
  }

  public set enteredFilterValue(value: string) {
    this._enteredFilterValue = value;
  }
  public get enteredFilterValue(): string {
    return this._enteredFilterValue;
  }

  public get groupFieldDelimiter() {
    return this._grpDelimiter;
  }

  public set imageUrl(value: any) {
    this._value = this.lmModel.lmModelServiceAdapter.toImageUrlValue(value);
  }

  public get isCompoundField() {
    return this._isCompoundField;
  }

  public get isNestedCompoundField() {
    return this._isNestedCompoundField;
  }

  public get isKeyFieldMultiselect() {
    return this._isMultiSelectKeyField;
  }

  public get isManualRep() {
    return this._isManualRepField;
  }

  public get isNumericArray() {
    return this._isNumericArray;
  }

  public get label() {
    return this._label;
  }

  public get maxSelected() {
    return this._maxSelected;
  }

  public get members() {
    return this._members;
  }

  public get missingImage() {
    return this._missingImage;
  }

  public get multiPartFile() {
    return this._multiPartFile;
  }

  // TODO: unable to set type of File?
  public set multiPartFile(file: File | Blob) {
    this._multiPartFile = file;
  }

  public get mutable() {
    return this._mutable;
  }

  public get mutexFieldId() {
    return this._mutexFieldId;
  }

  public get originalValue(): any {
    return this._originalValue;
  }

  public get precision() {
    return this._precision;
  }

  public get required() {
    return this._required;
  }

  public get scannable(): boolean {
    return this.specField.scannable;
  }

  public get scannableName(): string {
    return this.specField.scannableName;
  }

  public get size() {
    return this._size;
  }

  public get specLabel() {
    return (this.specField.label && this.specField.label.resourceValue)
      ? this.specField.label
      : this.specField.bindId;
  }

  public get states() {
    return this._stateValues;
  }

  public set states(stateValues) {
    this._stateValues = stateValues;
  }

  /**
   * Returns the localized value (display)
   * @returns
   */
  public get value() {
    return this._value;
  }

  /**
   * Sets the localized value
   * @param value
   */
  public set value(value: any) {
    // on search forms, boolean fields are treated like dropdowns and allowed to have a blank value. So don't call
    // the toBoolean method
    if (this.specField.type === AppConstants.BOOLEAN && !this.lmModel.isSearchForm) {
      this._value = this.lmModel.lmModelServiceAdapter.toBoolean(value);
    } else {
      this._value = value;
    }

    if (this._isCompoundField) {
      // When (1) The user enters or updates value manually,
      // or (2) The user selects the values from a select list,
      // Do not process member fields if returning from compact field members dialog

      // Only model is updated and not dataview
      // model field has the information if it is a group field and what the members are.
      this.lmModel.lmModelServiceAdapter.parseGroupFieldMembers(this, this.lmModel);
    }

    this.valueChanged.next(this._value);
  }

  public get properties(): LMModelField {
    const output = {};

    // skip any methods; keep booleans, numbers, and strings
    Object.keys(this).forEach((prop) => {
      if (typeof this[ prop ] === 'boolean'
        || typeof this[ prop ] === 'number'
        || typeof this[ prop ] === 'string'
        || Array.isArray(this[ prop ])) {
        output[ prop ] = this[ prop ];
      }
    });

    return output as LMModelField;
  }

  public set properties(modelFieldCache: LMModelField) {
    this.initialize();

    Object.keys(modelFieldCache).forEach((prop) => {
      this[ prop ] = modelFieldCache[ prop ];
    });
  }

  // Determines if the current value of the field is the blank value
  public get isBlank(): boolean {
    const isValueBlank = this.lmModel.lmModelServiceAdapter.isBlank(this.specField.type, this._value);

    if (this.specField.type === AppConstants.BINARYDOCUMENT) {return !this.multiPartFile && isValueBlank;}

    return isValueBlank;
  }

  public get docTitleFieldId() {
    return this._docTitleFieldId;
  }

  public set docTitleFieldId(docTitleFieldId) {
    this._docTitleFieldId = docTitleFieldId;
  }

  public get mimeTypeFieldId() {
    return this._mimeTypeFieldId;
  }

  public set mimeTypeFieldId(mimeTypeFieldId) {
    this._mimeTypeFieldId = mimeTypeFieldId;
  }

  public get mimeType() {
    return this._mimeType;
  }

  public set mimeType(mimeType) {
    this._mimeType = mimeType;
  }

  public set isManualRepField(isManualRepField: boolean) {
    this._isManualRepField = isManualRepField;
  }

  public set dependentTimeZone(fieldIds: string[]) {
    this._dependentTimeZone = fieldIds;
  }

  public get dependentTimeZone(): string[] {
    return this._dependentTimeZone;
  }

  public set exportFilterCriteria(eFC: boolean) {
    this._exportFilterCriteria = eFC;
  }

  public get exportFilterCriteria(): boolean {
    return this._exportFilterCriteria;
  }

  public set disableTranslationFiltering(value: boolean) {
    this._disableTranslationFiltering = value;
  }

  public get disableTranslationFiltering(): boolean {
    return this._disableTranslationFiltering;
  }

  // public methods
  public destroy() {
    this._applyState$.unsubscribe();
    this._applyState$ = null;

    this._clearState$.unsubscribe();
    this._clearState$ = null;

    this._errorMessage$.unsubscribe();
    this._errorMessage$ = null;

    this._valueChanged$.unsubscribe();
    this._valueChanged$ = null;
  }

  /**
   * Returns the data value; (Landmark)
   * @returns
   */
  public getData(): any {
    return this._value;
  }

  public setBlank() {
    this.value = this.lmModel.lmModelServiceAdapter.toBlankValue(this.specField.type);
    this.valueChanged.next(this._value);
  }

  // called from components where ngModel is split
  public toDataValue(value: any, transformOptions?: LMTransformOptions) {
    // use public setter here
    this.value = this._toDataValue(value, this.specField, transformOptions);
    this.valueChanged.next(this._value);
  }

  public get modelSpecField(): LMSpecField {
    return this.specField;
  }

  /**
   * Sets dataView field information onto the model field
   * @param                 dvField
   * @param                dvFields
   * @param opts
   */
  public setData(dvField: LMField, dvFields: LMFields, opts?: LMModelSetFieldsOptions) {
    // seems like im not getting the correct inline search after configuration
    let fieldIsMutable = false;

    if (opts?.resetData) {
      this.initialize();

      if (dvField.hasOwnProperty('value')) {
        this._originalValue = dvField.value;
      }

      // When FieldOptions.resetData is true, fire clear state.
      // This is used by LMTextComponent to clear the previous value stored in ngModelChangedValue member.
      this._clearState$.next(true);
    }

    if (opts?.filterCriteria) {
      this._filterCriteria = opts.filterCriteria.find(criteria => criteria.fieldId === this.specField.bindId);
    } else {
      this._filterCriteria = undefined;
    }

    if (this._filterCriteria) {
      // filter criteria exists for this field, set the filterType to the operator to the one from the filter criteria
      this._filterType = this._filterCriteria.operator;
    }

    if (opts?.alphaDefaultFilterCondition) {
      this._defaultFilterType = opts.alphaDefaultFilterCondition;
    }

    if (dvField.hasOwnProperty('size')) {
      this._size = dvField.size;
    }

    // TODO: evaluate - this._decSize is not referenced
    if (dvField.hasOwnProperty('decSize')) {
      this._decSize = dvField.decSize;
    }

    if (dvField.hasOwnProperty('precision')) {
      this._precision = dvField.precision;  // TODO look at refactoring this, the precsion meta data is on the specfield
    }

    if (dvField.hasOwnProperty('disableFieldHistory')) {
      this._displayFieldHistory = false;
    }

    if (dvField.hasOwnProperty('fireInitialValue')) {
      this._fireInitialValue = dvField.fireInitialValue;
    }

    if (dvField.hasOwnProperty('label')) {
      this._label = dvField.label;
    }

    // tri-state: null, false, true
    if (dvField.hasOwnProperty('required')) {
      // strict equality due to tri-state
      this._required = this.lmModel.lmModelServiceAdapter.modelActionState.isRequired === true && dvField.required === true;
    }

    if (dvField.hasOwnProperty('mutable')) {
      fieldIsMutable = this.lmModel.lmModelServiceAdapter.getMutableState(this.specField, dvField);
      this._mutable = fieldIsMutable;
    }

    if (dvField.hasOwnProperty('missingImage')) {
      this._missingImage = dvField.missingImage;
    }

    if (dvField.hasOwnProperty('mutexFieldId')) {
      this._mutexFieldId = dvField.mutexFieldId;
    }

    if (dvField.hasOwnProperty('type')) {
      this._isCompoundField = this.lmModel.lmModelServiceAdapter.isCompoundField(dvField, this.specField.isMultiselect);
    }

    if (dvField.hasOwnProperty('nested')) {
      this._isNestedCompoundField = true;
    }

    if (dvField.hasOwnProperty('delimiter')) {
      this._grpDelimiter = dvField.delimiter;
    }

    if (dvField.hasOwnProperty('members')) {
      // Group fields amd multiSelect fields have members
      this._members = dvField.members;
    }

    // if ((this.specField.hasUserDefinedStates || dvField.stateValues)
    if (this.specField.hasUserDefinedStates
      && this.specField.isMultiselect
      && Array.isArray(this._members)
    ) {
      // a specField indicating it has user-defined states
      // and is multi-select
      // and members exist on the data view
      // so set the selected state values
      // the multi-select dropdown will retrieve the values when clicked

      this._maxSelected = this._members.length;

      const hasStateValues: boolean = (this._stateValues !== undefined);

      // if this._stateValues already exists, then use it and don't rebuild
      const stateValues: Array<LMStateValue> = (!hasStateValues) ? [] : this._stateValues;
      const values = [];

      if (this._members.length > 0) {
        const dvMember = dvFields[ this._members[ 0 ] ];
        this._isNumericArray = (dvMember.hasOwnProperty('isNumber') || typeof dvMember.value === 'number');
      }

      for (let index = 0, len = this._members.length; index < len; index++) {
        const dvMember = dvFields[ this._members[ index ] ];
        const blankOrZero = this._isNumericArray ? 0 : '';

        // using non-strict equality to evaluate 0, '0', 0.00, '0.00' as zero
        // eslint-disable-next-line eqeqeq
        if (dvMember && dvMember.value != blankOrZero) {
          values.push(dvMember.value.toString());

          // if this._stateValues does not exist, then populate the entries
          if (!hasStateValues && dvMember.stateValues) {
            dvMember.stateValues.forEach((state) => {
              stateValues.push(state);
            });

          } else if (!hasStateValues && dvMember.stateValues === undefined) {
            // maf when LMRK-86561 is complete, this can be removed
            // LMCLIENT-23932: A multiselect that is a transient field that has value derived from does not return
            // the stateValues for its members, so add the stateValues with dvMember.value as the label and value
            stateValues.push( { label: dvMember.value.toString(), value: dvMember.value.toString() } );
          }
        }
      }

      // sets the type for multiselect to array
      this._value = values;
      this._stateValues = this.lmModel.lmModelServiceAdapter.fieldStatesToMultiOptionsArray(stateValues, values);
      this.valueChanged.next(this._value);
      // return here as value is set
      return;

    }
    if (
      this.lmModel.isSearchForm
      && this.specField.hasUserDefinedStates
      && this.specField.isMultiselect
      && this._members === undefined
    ) {
      // This is a search form with a keyfield that is showing as a multiselect
      this._isMultiSelectKeyField = true;

      // dataview field will have values when we are navigating back to a list that has filterCriteria set on it
      if (dvField.hasOwnProperty('stateValues')) {
        this.states = dvField.stateValues;
      }

    } else if (
      this.specField.hasUserDefinedStates
      && !fieldIsMutable
      && !dvField.hasOwnProperty('stateValues')
      && !dvField.hasOwnProperty('mutexFieldId')
    ) {
      // a non-mutable specField indicating it has user-defined states
      // and has no state values on the data view
      // and is not a mutex field
      // so retrieve the state values

      // TODO: non-mutable specField indicating it has user-defined states
      // this.lmModel.lmModelServiceAdapter.lmLoggerService.error([this, '//TODO: non-mutable specField indicating it has user-defined states', this.specField.bindId]);

    } else if (
      this.specField.hasUserDefinedStates
      && fieldIsMutable
      && String(dvField.value).trim().length > 0
      && !dvField.hasOwnProperty('stateValues')
    ) {
      // a mutable specField indicating it has user-defined states
      // and the data view value length is > 0
      // and has no state values on the data view
      // so retrieve the state values

      // TODO: mutable specField indicating it has user-defined states
      // this.lmModel.lmModelServiceAdapter.lmLoggerService.error([ this, '//TODO: mutable specField indicating it has user-defined states', this.specField.bindId ]);

    } else if (
      (this.specField.hasUserDefinedStates || dvField.hasShowAsDropdownStates)
      && dvField.hasOwnProperty('stateValues')
      && !this.specField.generated
    ) {
      // user-defined state field and display as dropdown fields.
      // don't add an empty entry as this stateValues array is for the dataView value
      // empty entries are done on the server in JSONConverter.java > fieldStateArrayForFieldValueNg()
      // and in lm-dropdown.component.ts when the user-defined result is processed
      this._stateValues = this.lmModel.lmModelServiceAdapter.fieldStatesToOptionsArray(dvField.stateValues, false);

      // maf - 07/31/2018 commenting out as not sure the following checks are needed
      // } else if (dvField.hasOwnProperty('stateValues') && this.specField.generated) {
      //   // this is a member of an occurs field
      //   // no-op
      //
      // } else if (dvField.hasOwnProperty('stateValues') && !this.specField.generated) {
      //   // not sure what this will catch
      //   this._stateValues = this.lmModel.lmModelServiceAdapter.fieldStatesToOptionsArray(dvField.stateValues, true);

    } else if (this.specField.hasOwnProperty('stateValues')) {
      // static state field
      // need to format the zero value for BCD and PERCENT types as they need to match
      // the dataView field value for the track dirty processing to work correctly.
      // empty entries are done in ViewBuilderBase.java > fieldAttributeToJsonObject()

      if (this.specField.type === AppConstants.BCD
        || this.specField.type === AppConstants.PERCENT) {
        // fix the zero value so it matches the dataView value
        for (let index = 0, len = this.specField.stateValues.length; index < len; index++) {
          const stateValue: LMStateValue = this.specField.stateValues[ index ];

          // using non-strict equality to evaluate 0, '0', 0.00, '0.00' as zero
          // eslint-disable-next-line eqeqeq
          if (stateValue.value == 0) {
            // using precision here as that is the property available for a state
            stateValue.value = this.lmModel.lmModelServiceAdapter.fieldStateFormatZeroValue(this.specField.type, dvField.precision);
            break;
          }
        }
      }

      // Add a blank entry for cube search form
      const tempStateValues = this.lmModel.lmModelServiceAdapter.fieldStatesToOptionsArray(this.specField.stateValues, this.lmModel.isCubeSearchForm ? true : false);

      if (this.lmModel.isSearchForm && !this.specField.isBusClassContextField) {
        // Change blank entries to Not Entered when on a search form
        // using non-strict equality to evaluate 0, '0', 0.00, '0.00' as zero, etc
        // eslint-disable-next-line eqeqeq
        if ((tempStateValues[ 0 ].value == 0 && tempStateValues[ 0 ].label.trim() === '') ||
          tempStateValues[ 0 ].value === ' ') {
          tempStateValues[ 0 ].value = 'Not Entered';
          tempStateValues[ 0 ].label = Soho.Locale.translate('ListStaticFieldFilterNotEntered');
        }
      }
      this._stateValues = tempStateValues;

    } else if (this.lmModel.isSearchForm
      && this.specField.type === AppConstants.BOOLEAN) {
      // BOOLEAN fields are states on a search form
      this._stateValues = this.lmModel.lmModelServiceAdapter.booleanSearchStates();
    }

    if (this.specField.scannable) {
      this._scannable = true;
      if (this.specField.scannableName) {
        this._scannableName = this.specField.scannableName;
      }
    }

    if (dvField.hasOwnProperty('docTitleFieldId')) {
      this._docTitleFieldId = dvField.docTitleFieldId;
    }

    if (dvField.hasOwnProperty('mimeTypeFieldId')) {
      this._mimeTypeFieldId = dvField.mimeTypeFieldId;

      if (dvField.hasOwnProperty('mimeType')) {
        this._mimeType = dvField.mimeType;
      }
    }

    // NOTE: value should be the last property evaluated

    if (dvField.hasOwnProperty('value')) {
      let value = dvField.value;

      //  This is causing the modelField to be marked as dirty since _originalValue doesn't
      //  match _value when the dataview field contains a &gt; or &lt;. The character is displayed correctly on the
      //  preview mode for richtext, and seen as the correct &gt; or &lt; on the HTML mode. So I think this is
      //  no longer needed. https://jira.infor.com/browse/LMWEB-18388
      // for rich text fields, need to unescape any embedded < >
      // if (dvField.hasOwnProperty('isRichText')) {
      //   value = value.toString();
      //
      //   if (value.indexOf('&gt;') > -1) {
      //     value = value.split('&gt;').join('>');
      //   }
      //
      //   if (value.indexOf('&lt;') > -1) {
      //     value = value.split('&lt;').join('<');
      //   }
      // }

      // For initial values on search forms, the value can be a string. If it's a dropdown, this value needs to be
      // converted to an array
      // Business class context fields does not allow multi select, so the value is not changed to an array. LMWEB-16395
      if (this.lmModel.isSearchForm && (this.specField.stateValues?.length > 0 || this.specField.hasUserDefinedStates || dvField.hasShowAsDropdownStates) && !this.specField?.isBusClassContextField) {
        if (!this.lmModel.lmModelServiceAdapter.isBlank(this.specField.type, value)) {
          if (this.specField.isMultiselect) {
            // This is the case where a keyfield is displaying as a multi select on a SearchForm. Since this.specField.type
            // is undefined in this case, we need to check if the incoming value is the blank value again.
            // TODO figure out why the specField doesn't have a type for multiselect keyfields on a search form
            // eslint-disable-next-line eqeqeq
            if (value != '' && value != 0) {
              value = Array.isArray(value) ? value : [ value ] as Array<string>;
            }
          } else {
            value = Array.isArray(value) ? value : [ value ] as Array<string>;
          }
        }
      }

      // if numeric, percent or bcd on a search form, set value to empty string and not zero
      if ((this.specField.type === AppConstants.BCD || this.specField.type === AppConstants.PERCENT || this.specField.type === AppConstants.NUMERIC) && this.lmModel.isSearchForm) {

        // need non strict equality so 0, '0', '0.00' are all seen as zero. Only set to blank if there isn't a filtercriteria, since 0 can be searched on
        // eslint-disable-next-line eqeqeq
        if (!this._filterCriteria && value == 0) {
          value = '';
        }
      }

      if (this.specField.type === AppConstants.IMAGE) {
        this.imageUrl = value;

      } else {
        this._value = value;
      }

      this.valueChanged.next(this._value);
    }
    // NOTE: value should be the last property evaluated
  }
}
